/**
 * Calendar-day arithmetic helpers used by the EventTime component.
 *
 * Days and weeks are always computed in UTC so the rendered output is stable
 * across SSR (Vercel runs in UTC) and the visitor's local timezone — the same
 * reasoning behind `parseTime`'s naive-ISO fallback in `~/ui/formatted`.
 */

import { parseTime } from '~/ui/formatted';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Midnight UTC of the calendar day the given Date falls on. */
export function startOfDayUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** True when both dates fall on the same UTC calendar day. */
export function isSameDayUTC(a: Date, b: Date): boolean {
  return startOfDayUTC(a).getTime() === startOfDayUTC(b).getTime();
}

/**
 * Inclusive day-span between earliest and latest `startTime` in sessions.
 * - 1 session → 1
 * - 2 sessions on the same day → 1
 * - 2 sessions one day apart → 2
 * - 3 sessions spanning a week → 7
 *
 * Floors at 1; mirrors `weekSpan` but in days rather than weeks.
 */
export function daySpan(sessions: { startTime: string }[]): number {
  if (sessions.length === 0) return 1;
  // `parseTime` (not `Date.parse` directly) so a naive/offset-less ISO
  // string is always read as UTC — per spec, `Date.parse` treats it as
  // LOCAL time, which made this parse differently on the UTC SSR process
  // than in a visitor's browser (US-12212).
  const times = sessions
    .map((s) => parseTime(s.startTime).getTime())
    .filter((n) => !Number.isNaN(n));
  if (times.length < 2) return 1;
  const earliest = startOfDayUTC(new Date(Math.min(...times))).getTime();
  const latest = startOfDayUTC(new Date(Math.max(...times))).getTime();
  const days = Math.floor((latest - earliest) / MS_PER_DAY);
  return Math.max(1, days + 1);
}

/**
 * Number of weeks an event's sessions span, inclusive of both endpoints.
 * Examples:
 * - 2 sessions one day apart → 1 week
 * - 6 sessions one per week over 6 weeks → 6 weeks
 * Floors at 1; only meaningful when `sessions.length >= 2`.
 */
export function weekSpan(sessions: { startTime: string }[]): number {
  if (sessions.length < 2) return 1;
  // `parseTime`, not `Date.parse` — see `daySpan` above for why.
  const times = sessions
    .map((s) => parseTime(s.startTime).getTime())
    .filter((n) => !Number.isNaN(n));
  if (times.length < 2) return 1;
  // Normalise to UTC day boundaries before dividing, the way `daySpan`
  // already does. Without this, a DST transition anywhere inside the span
  // shifts the raw millisecond delta by an hour (the two endpoints parse
  // under different UTC offsets), `Math.floor` silently drops a day, and
  // that flips the `Math.ceil((days + 1) / 7)` week boundary — this is what
  // produced the 39-vs-38-week hydration mismatch in US-12212.
  const earliest = startOfDayUTC(new Date(Math.min(...times))).getTime();
  const latest = startOfDayUTC(new Date(Math.max(...times))).getTime();
  const days = Math.floor((latest - earliest) / MS_PER_DAY);
  return Math.max(1, Math.ceil((days + 1) / 7));
}
